/**
 * Score validation utilities
 * Ensures consistency between database scores and calculated breakdowns
 */

import type { TraitResults } from '../types';
import { calculateScoreBreakdown } from '../scoring/score-breakdown';

export interface ScoreValidationResult {
  isValid: boolean;
  issues: string[];
  storedScore: number;
  calculatedScore: number;
  difference: number;
}

/**
 * Validate that the stored score matches the stored breakdown (if available)
 * Falls back to calculating breakdown for old messages without stored breakdown
 * @param storedScore - Score from database (already rounded)
 * @param traits - Trait results from database
 * @param reasons - Reasons array from database
 * @param threshold - User threshold for comparison
 * @returns Validation result with any issues found
 */
export function validateScoreConsistency(
  storedScore: number,
  traits: TraitResults,
  reasons: string[],
  threshold: number
): ScoreValidationResult {
  const issues: string[] = [];

  // Use stored breakdown if available (more accurate), otherwise calculate
  const breakdown = traits.score_breakdown || calculateScoreBreakdown(traits, reasons);
  const calculatedScore = Math.round(breakdown.total);

  // Check if scores match (within rounding tolerance)
  const difference = Math.abs(storedScore - calculatedScore);
  const isValid = difference <= 1; // Allow 1 point difference for rounding edge cases

  if (!isValid) {
    issues.push(
      `Score mismatch: stored=${storedScore}, breakdown=${calculatedScore}, difference=${difference}`
    );
  }

  // Check if labeled status matches threshold
  // Note: We can't check actual labeled status here without the message object

  // Check if threshold in traits matches passed threshold
  if (traits.user_context?.threshold !== undefined && traits.user_context.threshold !== threshold) {
    issues.push(
      `Threshold mismatch: traits=${traits.user_context.threshold}, current=${threshold}`
    );
  }

  return {
    isValid,
    issues,
    storedScore,
    calculatedScore,
    difference,
  };
}

/**
 * Log validation warnings in development mode
 */
export async function logScoreValidation(
  validation: ScoreValidationResult,
  messageId: string,
  userId: string
): Promise<void> {
  if (process.env.NODE_ENV === 'development' && !validation.isValid) {
    const { logger } = await import('../logger');
    logger.warn('Score validation failed', {
      messageId,
      userId,
      issues: validation.issues,
      storedScore: validation.storedScore,
      calculatedScore: validation.calculatedScore,
      difference: validation.difference,
    });
  }
}
